import { inject, Injectable, Injector } from "@angular/core";
import { environment } from "@environments/environment";
import { scrub, scrubText } from "@core/logging/log-scrubber";
import { ErrorReporter } from "@core/logging/logger.types";
import { ErrorContextBuilder } from "@core/logging/error-context";

const MAX_REPORTS_PER_MINUTE = 10;
const MAX_MESSAGE_LENGTH = 4000;
const SERVICE_NAME = "passlink-frontend";

/**
 * GCP Error Reporting REST transport (see docs/logging/02-error-tracking.md).
 *
 * Must never inject LoggerService (it is injected BY LoggerService via the
 * ERROR_REPORTER token — single-funnel rule) and must never throw or surface
 * its own failures: a broken reporter reporting itself would recurse.
 * Uses fetch, not HttpClient, so reports bypass the app's interceptors.
 */
@Injectable({ providedIn: "root" })
export class ErrorReporterService implements ErrorReporter {
  // Resolved lazily at capture time: this service is constructed DURING
  // LoggerService construction (via the ERROR_REPORTER token), and eagerly
  // injecting ErrorContextBuilder → AuthService → LoggerService is an NG0200
  // circular-dependency error.
  private readonly injector = inject(Injector);

  private windowStartTs = 0;
  private reportsInWindow = 0;
  private lastSignature = "";

  captureException(
    error: unknown,
    loggerContext?: Record<string, unknown>
  ): void {
    try {
      if (!this.isEnabled()) {
        return;
      }

      const message = scrubText(this.toMessage(error), MAX_MESSAGE_LENGTH);
      if (!this.takeBudget(message.split("\n", 1)[0])) {
        return;
      }

      const body = JSON.stringify(
        this.buildEvent(error, message, loggerContext)
      );
      fetch(this.endpoint(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Never let the reporter become an error source of its own.
    }
  }

  private isEnabled(): boolean {
    const { LOG_CONFIG, errorReporting } = environment;
    return (
      LOG_CONFIG.remote === true &&
      !!errorReporting?.projectId &&
      !!errorReporting?.apiKey
    );
  }

  private endpoint(): string {
    const { projectId, apiKey } = environment.errorReporting;
    return `https://clouderrorreporting.googleapis.com/v1beta1/projects/${projectId}/events:report?key=${apiKey}`;
  }

  /**
   * One report per error signature per minute, MAX_REPORTS_PER_MINUTE overall —
   * an error loop in a render cycle must not flood the API quota.
   */
  private takeBudget(signature: string): boolean {
    const now = Date.now();
    if (now - this.windowStartTs > 60_000) {
      this.windowStartTs = now;
      this.reportsInWindow = 0;
      this.lastSignature = "";
    }

    if (
      signature === this.lastSignature ||
      this.reportsInWindow >= MAX_REPORTS_PER_MINUTE
    ) {
      return false;
    }

    this.lastSignature = signature;
    this.reportsInWindow++;
    return true;
  }

  private toMessage(error: unknown): string {
    if (error instanceof Error) {
      // V8 stacks already start with "Name: message"; keep them verbatim so
      // Error Reporting can parse frames and group by stack.
      if (error.stack) {
        return error.stack.startsWith(error.name)
          ? error.stack
          : `${error.name}: ${error.message}\n${error.stack}`;
      }
      return `${error.name}: ${error.message}`;
    }
    if (typeof error === "string") {
      return error;
    }
    try {
      return JSON.stringify(error) ?? String(error);
    } catch {
      return String(error);
    }
  }

  private buildEvent(
    error: unknown,
    message: string,
    loggerContext?: Record<string, unknown>
  ): Record<string, unknown> {
    const ctx = this.injector.get(ErrorContextBuilder).build();
    const hasStack = error instanceof Error && !!error.stack;

    // Error Reporting has no custom-context fields: the scrubbed context
    // travels as a JSON block appended after the stack (stack-based grouping
    // ignores it; for stack-less events grouping falls back to reportLocation).
    const details = scrub({ ...ctx, logger: loggerContext });

    return {
      serviceContext: { service: SERVICE_NAME, version: ctx.appVersion },
      message: `${message}\n\ncontext: ${JSON.stringify(details)}`,
      context: {
        httpRequest: { url: ctx.route },
        user: ctx.userRole ?? "anonymous",
        ...(hasStack
          ? {}
          : {
              reportLocation: {
                filePath: this.toFilePath(loggerContext?.["context"]),
                lineNumber: 0,
                functionName: "unknown",
              },
            }),
      },
    };
  }

  /**
   * reportLocation.filePath must be a plain string; a non-string context
   * (e.g. an object) would otherwise stringify to "[object Object]".
   */
  private toFilePath(context: unknown): string {
    if (context == null) {
      return "unknown";
    }
    if (typeof context === "string") {
      return context;
    }
    try {
      return JSON.stringify(context) ?? "unknown";
    } catch {
      return "unknown";
    }
  }
}
